import { connect } from 'react-redux';
import { push } from 'redux-first-history';

import PaymentInfoPageV2 from './component';
import { getRachelDashboard } from '@renter-hub/actions/requests';
import { pageFlows } from '@renter-hub/pages/PaymentSettingsPage/pageFlows';
import { showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import {
  DEFAULT_ERROR,
  NEW_PAYMENT_METHOD_ADDED_SUCCESS,
  PAYMENT_METHOD_UNPROCESSABLE,
  PLAID_CREATE_PAYMENT_METHOD_ERROR,
} from '@renter-hub/constants/notifications';
import HTTP_STATUS from '@common/constants/HttpConstants';
import paths from '@renter-hub/pathsManifest';
import type { ErrorInterface } from '@common/types';
import { logError } from '@zg-rentals/log-error';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';

const mapStateToProps = (state, { fromDashboard }) => ({
  ...state.payments.renter.onboarding.paymentInfo,
  name: state.payments.renter.dashboard.renterProfile.name,
  config: state.payments.renter.config,
  fromDashboard,
  isColoradoCustomer: state.payments.renter.dashboard.isColoradoCustomer,
  paymentMethodTypes: state.payments.renter.dashboard.paymentMethodTypes,
  paymentMethods: state.payments.renter.dashboard.paymentMethods,
  user: state.user,
  listingAlias: state.payments?.renter?.dashboard?.listingAlias,
});

const mapDispatchToProps = (dispatch, { baseUrl, location, propertyId, brand, fromDashboard }) => ({
  onPaymentMethodCreationSuccess: async ({ paymentMethod }, paymentMethods = []) => {
    try {
      const paymentMethodInfo = {
        bankName: paymentMethod?.displayName || '',
        id: paymentMethod?.id || '',
        isBankAccountVerificationNeeded: paymentMethod?.isBankAccountVerificationNeeded || false,
      };

      const paymentMethodStatus = paymentMethod?.paymentMethodStatus || '';
      const paymentMethodNeedsVerification =
        paymentMethodStatus === VERIFICATION_STATUS.PENDING_AUTOMATIC.INTERNAL ||
        paymentMethodStatus === VERIFICATION_STATUS.PENDING_MANUAL.INTERNAL;
      const verifiedPaymentMethods = getVerifiedPaymentMethods(paymentMethods);
      const needsMicrodepositsVerification = paymentMethodNeedsVerification && verifiedPaymentMethods.length === 0;

      if (needsMicrodepositsVerification) {
        const extraParams = fromDashboard ? { fromDashboard: true } : {};
        await dispatch(getRachelDashboard({ propertyId }));
        await dispatch(
          push({ pathname: baseUrl + paths.plaidConnectPage, search: '?action=verify', state: extraParams }),
        );
        return;
      }

      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      const { moveInCostsDetails } = renterStatusResponse.payload.renterStatus || {};
      let toUrl;
      let search = '';
      let extraParams;
      const fromWhere = location?.state?.fromWhere;
      const typeOfPaymentToMake = location?.state?.typeOfPaymentToMake;

      if (fromWhere === paths.editPaymentSettingsPage) {
        toUrl = baseUrl + paths.editPaymentSettingsPage;
        extraParams = {
          pageFlowOverride: pageFlows.SelectPaymentMethodPage,
          savedFrequencyType: location?.state?.savedFrequencyType,
        };
      } else if (fromWhere === paths.updatePaymentMethodsPage) {
        toUrl = baseUrl + paths.updatePaymentMethodsPage;
        extraParams = {
          paymentMethodId: paymentMethodInfo.id,
          typeOfPaymentToMake,
        };
      } else if (fromWhere === paths.editMoveInCostsSchedulePage) {
        toUrl = baseUrl + paths.editMoveInCostsSchedulePage;
        extraParams = {
          paymentMethodId: paymentMethodInfo.id,
        };
      } else if (fromWhere === paths.oneTimePaymentPage) {
        // Check OTP before fromDashboard: the modal route always passes fromDashboard, so this must take
        // precedence to return to the same OTP request (otpKey is required to resolve it).
        const otpKey = location?.state?.otpKey;
        toUrl = baseUrl + paths.oneTimePaymentPage;
        search = otpKey ? `?otpKey=${encodeURIComponent(otpKey)}` : '';
      } else if (fromDashboard) {
        toUrl = baseUrl;
      } else if (moveInCostsDetails?.items?.length > 0) {
        toUrl = baseUrl + paths.moveInCostsSchedulingPage;
      } else {
        toUrl = baseUrl + paths.paymentSettingsPage;
      }

      await dispatch(push({ pathname: toUrl, search, state: extraParams ?? {} }));

      const notificationType =
        fromWhere === paths.updatePaymentMethodsPage || fromWhere === paths.editMoveInCostsSchedulePage
          ? showModalNotification
          : fromDashboard
            ? showPageNotification
            : null;

      if (notificationType) {
        await dispatch(
          notificationType(NEW_PAYMENT_METHOD_ADDED_SUCCESS, {
            bankName: paymentMethodInfo.bankName,
            needsVerification: paymentMethodInfo.isBankAccountVerificationNeeded,
          }),
        );
      }
    } catch (err) {
      // By default, show the default error banner
      let notificationType = DEFAULT_ERROR;
      const error = err as ErrorInterface;

      if (error.action && error.action.payload) {
        const { httpStatus, status } = error.action.payload;
        const resolvedStatus = httpStatus ?? status;

        // If 401 status, redirect to sign-in
        if (resolvedStatus === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }

        // If 422 status, show payment method unprocessable banner
        if (resolvedStatus === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY) {
          notificationType = PAYMENT_METHOD_UNPROCESSABLE;
        }
      }

      logError({ error: err });
      const showNotification = fromDashboard ? showModalNotification : showPageNotification;

      await dispatch(showNotification(notificationType));

      return { error: true };
    }
  },
  onPaymentMethodCreationError: async (error: unknown) => {
    const showNotification = fromDashboard ? showModalNotification : showPageNotification;

    logError({ error });
    // @ts-ignore
    if (error?.errorMessage) {
      // @ts-ignore
      await dispatch(showNotification(PLAID_CREATE_PAYMENT_METHOD_ERROR, { message: error?.errorMessage }));
    } else {
      await dispatch(showNotification(DEFAULT_ERROR));
    }
    return { error: true };
  },
  onCancel: () => {
    // redirect to either the previous page, or the dashboard (default)
    const fromWhere = location?.state?.fromWhere;
    let toUrl;
    if (fromWhere === paths.oneTimePaymentPage) {
      // Return to the same OTP request (otpKey is required to resolve it)
      const otpKey = location?.state?.otpKey;
      toUrl = `${baseUrl}${paths.oneTimePaymentPage}${otpKey ? `?otpKey=${encodeURIComponent(otpKey)}` : ''}`;
    } else if (
      fromWhere === paths.paymentSettingsPage ||
      fromWhere === paths.editPaymentSettingsPage ||
      fromWhere === paths.plaidConnectPage
    ) {
      toUrl = `${baseUrl}${fromWhere}`;
    } else {
      toUrl = baseUrl;
    }
    return dispatch(push(toUrl));
  },
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  ...dispatchProps,
  onPaymentMethodCreationSuccess: (payload) => {
    dispatchProps.onPaymentMethodCreationSuccess(payload, stateProps.paymentMethods);
  },
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(PaymentInfoPageV2);
